/**
 * User Detail Modal Component
 *
 * Modal for displaying detailed user information with three tabs:
 * 1. Database Info - Shows all user fields with edit functionality
 * 2. Olo Account - Shows Olo account status and SSO link management
 * 3. Punchh Account - Shows Punchh account info and creation
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { UserDatabaseTab } from './user-database-tab';
import { UserDevicesTab } from './user-devices-tab';
import { UserEditForm } from './user-edit-form';
import { UserOloTab } from './user-olo-tab';
import { UserPunchhTab } from './user-punchh-tab';
import { useUserById, useUpdateUser } from '@/hooks/use-user-management';
import { useToast } from '@/hooks/use-toast';
import type { UpdateUserRequest } from '@/types/user-management';

interface UserDetailModalProps {
  userId: string | null;
  onClose: () => void;
}

/**
 * User Detail Modal
 * Main modal component with tabbed interface
 */
export function UserDetailModal({ userId, onClose }: UserDetailModalProps) {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<'database' | 'devices' | 'olo' | 'punchh'>(
    'database'
  );
  const [isEditing, setIsEditing] = useState(false);

  const { data: userDetails, isLoading, error } = useUserById(userId);
  const updateUserMutation = useUpdateUser();

  const user = userDetails?.user;

  /**
   * Handle Edit User
   */
  const handleEdit = () => {
    setIsEditing(true);
  };

  /**
   * Handle Cancel Edit
   */
  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  /**
   * Handle Submit Edit
   */
  const handleSubmitEdit = async (data: UpdateUserRequest) => {
    if (!user) return;

    const result = await updateUserMutation.mutateAsync({ userId: user.id, data });

    const { syncResults } = result;
    const hasSyncFailure =
      syncResults !== undefined &&
      Object.values(syncResults).some(r => r !== undefined && !r.success);

    if (hasSyncFailure) {
      const failedSystems = Object.entries(syncResults!)
        .filter(([, r]) => r !== undefined && !r.success)
        .map(([system]) => system)
        .join(', ');
      toast({
        title: 'Phone partially synced',
        description: `Phone updated in database, but sync failed for: ${failedSystems}. Save again to retry.`,
        variant: 'destructive',
      });
    } else {
      toast({
        title: 'User updated',
        description: 'User information has been updated successfully',
      });
    }

    setIsEditing(false);
  };

  /**
   * Handle Close Modal
   */
  const handleClose = () => {
    if (isEditing) {
      const confirmClose = window.confirm(
        'You have unsaved changes. Are you sure you want to close?'
      );
      if (!confirmClose) return;
    }

    setIsEditing(false);
    setActiveTab('database');
    onClose();
  };

  return (
    <Dialog open={userId !== null} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>User Details</DialogTitle>
        </DialogHeader>

        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-12 space-y-2">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading user details...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>Failed to load user details: {error.message}</AlertDescription>
          </Alert>
        )}

        {/* User Data */}
        {user && (
          <Tabs value={activeTab} onValueChange={value => setActiveTab(value as typeof activeTab)}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="database">Database Info</TabsTrigger>
              <TabsTrigger value="devices">
                Devices
                {userDetails && userDetails.devices.length > 0 ? (
                  <span className="ml-1.5 text-xs text-muted-foreground">
                    ({userDetails.devices.length})
                  </span>
                ) : null}
              </TabsTrigger>
              <TabsTrigger value="olo">Olo Account</TabsTrigger>
              <TabsTrigger value="punchh">Punchh Account</TabsTrigger>
            </TabsList>

            {/* Database Info Tab */}
            <TabsContent value="database" className="space-y-4 mt-4">
              {isEditing ? (
                <UserEditForm
                  user={user}
                  onSubmit={handleSubmitEdit}
                  onCancel={handleCancelEdit}
                  isSubmitting={updateUserMutation.isPending}
                  error={updateUserMutation.error?.message || null}
                />
              ) : (
                <UserDatabaseTab user={user} onEdit={handleEdit} />
              )}
            </TabsContent>

            {/* Devices Tab */}
            <TabsContent value="devices" className="space-y-4 mt-4">
              <UserDevicesTab devices={userDetails?.devices ?? []} />
            </TabsContent>

            {/* Olo Account Tab */}
            <TabsContent value="olo" className="space-y-4 mt-4">
              <UserOloTab user={user} />
            </TabsContent>

            {/* Punchh Account Tab */}
            <TabsContent value="punchh" className="space-y-4 mt-4">
              <UserPunchhTab user={user} />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
